import { BookOpen, Boxes, FileText, Images, Megaphone, type LucideIcon } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchResultRow } from './SearchResultRow';
import { SEARCH_TYPES, type SearchEntityType, type SearchResult } from './search-api';

const TYPE_ICON: Record<SearchEntityType, LucideIcon> = {
  content_item: FileText,
  campaign: Megaphone,
  media_asset: Images,
  entry: Boxes,
  knowledge_item: BookOpen,
};

/** The permission a member needs to see a type at all (the server enforces it; the client only hides unusable chips). */
const TYPE_PERMISSION: Record<SearchEntityType, string> = {
  content_item: 'content:read',
  campaign: 'content:read',
  media_asset: 'media:read',
  entry: 'knowledge:read',
  knowledge_item: 'knowledge:read',
};

/** The types a member can search: those their permissions allow (entries of every kind, E470). */
export function readableSearchTypes(permissions: readonly string[]): SearchEntityType[] {
  return SEARCH_TYPES.filter((type) => permissions.includes(TYPE_PERMISSION[type]));
}

function Group({ type, results }: { type: SearchEntityType; results: SearchResult[] }) {
  const { t } = useTranslation();
  const headingId = useId();
  const Icon = TYPE_ICON[type];
  return (
    <section className="search-group" aria-labelledby={headingId}>
      <h2 id={headingId} className="search-group__title">
        <Icon aria-hidden="true" size={20} />
        {t(`search.types.${type}`)}
      </h2>
      <ul className="search-results">
        {results.map((result) => (
          <SearchResultRow key={`${result.entity_type}-${result.entity_id}`} result={result} />
        ))}
      </ul>
    </section>
  );
}

/** Results grouped by type (fixed order); inside a group the server's relevance order is kept. */
export function SearchResultGroups({ results }: { results: readonly SearchResult[] }) {
  return (
    <div className="ui-stack">
      {SEARCH_TYPES.map((type) => {
        const ofType = results.filter((result) => result.entity_type === type);
        return ofType.length > 0 ? <Group key={type} type={type} results={ofType} /> : null;
      })}
    </div>
  );
}
